import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Ksef } from "../lib/api";
import { toast } from "sonner";
import {
  Receipt, Settings2, Send, ShieldCheck, AlertCircle, Loader2,
  ExternalLink, KeyRound, Check, FileStack, Download,
} from "lucide-react";
import SalesInvoices from "../components/SalesInvoices";
import PurchaseInvoices from "../components/PurchaseInvoices";
import { PageSkeleton } from "../components/PageSkeleton";

/**
 * Accounting hub — Phase 1 exposes KSeF Settings + Connection Test only.
 * Sales / Purchase invoice tabs are placeholders for Phase 2 / 3.
 */
export default function Accounting() {
  const [params, setParams] = useSearchParams();
  const tab = ["settings", "sales", "purchases"].includes(params.get("tab")) ? params.get("tab") : "settings";
  const setTab = (value) => setParams({ tab: value });

  const tabs = [
    { key: "settings", label: "Ustawienia KSeF", icon: Settings2, testId: "acct-tab-settings" },
    { key: "sales", label: "Faktury sprzedaży", icon: FileStack, testId: "acct-tab-sales" },
    { key: "purchases", label: "Faktury zakupu", icon: Download, testId: "acct-tab-purchases" },
  ];

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8" data-testid="accounting-page">
      <header className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 border-2 border-zinc-900 bg-white flex items-center justify-center shadow-[4px_4px_0_0_#18181b]">
            <Receipt className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              // Moduł księgowy — KSeF 2.0 · FA(3)
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-black tracking-tight">
              Księgowość
            </h1>
          </div>
        </div>
        <p className="text-sm text-zinc-600 max-w-2xl">
          Integracja z Krajowym Systemem e-Faktur. Wystawianie i pobieranie faktur bezpośrednio z KSeF,
          numer KSeF + UPO zapisywane automatycznie w bazie.
        </p>
      </header>

      <nav
        className="flex flex-wrap gap-1 mb-6 border-b-2 border-zinc-900"
        data-testid="accounting-tabs"
      >
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={t.testId}
              onClick={() => !t.soon && setTab(t.key)}
              disabled={t.soon}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wider border-2 border-b-0 -mb-[2px] transition-colors
                ${active
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-600 border-transparent hover:border-zinc-300 hover:text-zinc-900"}
                ${t.soon ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              {t.soon && (
                <span className="ml-1 px-1.5 py-0.5 text-[9px] font-mono bg-amber-100 text-amber-900 rounded-sm">
                  WKRÓTCE
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {tab === "settings" && <SettingsTab />}
      {tab === "sales" && <SalesInvoices />}
      {tab === "purchases" && <PurchaseInvoices />}
    </div>
  );
}

function SoonPlaceholder({ title }) {
  return (
    <div className="border-2 border-dashed border-zinc-300 p-12 text-center bg-white">
      <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-400 mb-3">
        Faza 2/3
      </div>
      <h3 className="text-xl font-heading font-black uppercase tracking-tight mb-1">{title}</h3>
      <p className="text-sm text-zinc-500">
        Dostępne po skonfigurowaniu i przetestowaniu połączenia z KSeF.
      </p>
    </div>
  );
}

function SettingsTab() {
  const [environments, setEnvironments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ environment: "test", nip: "", token: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    Promise.all([Ksef.environments(), Ksef.getSettings()])
      .then(([envs, s]) => {
        setEnvironments(envs);
        setSettings(s);
        setForm({ environment: s.environment || "test", nip: s.nip || "", token: "" });
      })
      .catch((e) => toast.error("Błąd ładowania: " + (e.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, []);

  const currentEnv = environments.find((e) => e.key === form.environment);

  const handleSave = async () => {
    if (!form.nip.trim()) {
      toast.error("NIP jest wymagany");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        environment: form.environment,
        nip: form.nip.replace(/[^0-9]/g, ""),
      };
      if (form.token && form.token.trim()) payload.token = form.token.trim();
      const updated = await Ksef.saveSettings(payload);
      setSettings(updated);
      setForm((prev) => ({ ...prev, token: "" }));
      toast.success("Ustawienia zapisane");
    } catch (e) {
      toast.error("Błąd zapisu: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    const tid = toast.loading("Łączenie z KSeF...");
    try {
      const r = await Ksef.testConnection();
      setTestResult({ ok: true, ...r });
      toast.success("Uwierzytelnianie KSeF udane", { id: tid });
      // Refresh settings to pick up last_connected_at
      Ksef.getSettings().then(setSettings);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setTestResult({ ok: false, message: msg });
      toast.error("Błąd KSeF: " + msg, { id: tid });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <PageSkeleton label="Ładuję konfigurację…" rows={3} />
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* LEFT — configuration form */}
      <div className="lg:col-span-2 bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#18181b]">
        <div className="px-5 py-4 border-b-2 border-zinc-900 bg-zinc-50 flex items-center gap-2">
          <Settings2 className="w-4 h-4" />
          <h2 className="font-heading font-black uppercase tracking-tight text-base">
            Konfiguracja KSeF
          </h2>
        </div>

        <div className="p-5 space-y-6">
          {/* Environment */}
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-2">
              Środowisko
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {environments.map((e) => {
                const active = form.environment === e.key;
                return (
                  <label
                    key={e.key}
                    data-testid={`env-radio-${e.key}`}
                    className={`border-2 px-3 py-3 cursor-pointer transition-colors ${
                      active
                        ? "border-zinc-900 bg-zinc-900 text-white"
                        : "border-zinc-300 bg-white hover:border-zinc-500"
                    }`}
                  >
                    <input
                      type="radio"
                      name="env"
                      value={e.key}
                      checked={active}
                      onChange={() => setForm({ ...form, environment: e.key })}
                      className="sr-only"
                    />
                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">
                      {e.key}
                    </div>
                    <div className="text-sm font-bold">{e.label}</div>
                    <div
                      className={`text-[10px] font-mono truncate mt-0.5 ${
                        active ? "text-white/60" : "text-zinc-400"
                      }`}
                    >
                      {e.base_url.replace(/^https?:\/\//, "")}
                    </div>
                  </label>
                );
              })}
            </div>
            {form.environment !== "test" && (
              <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-300 px-2.5 py-1.5">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  {form.environment === "prod"
                    ? "UWAGA: Środowisko produkcyjne — faktury wchodzą w obieg prawny natychmiast."
                    : "Środowisko Demo używa realnych danych firmy, ale bez skutków prawnych."}
                </span>
              </div>
            )}
          </div>

          {/* NIP */}
          <div>
            <label
              htmlFor="ksef-nip"
              className="block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5"
            >
              NIP firmy (kontekst logowania)
            </label>
            <input
              id="ksef-nip"
              type="text"
              inputMode="numeric"
              value={form.nip}
              maxLength={13}
              onChange={(e) => setForm({ ...form, nip: e.target.value })}
              placeholder="1234567890"
              data-testid="ksef-nip-input"
              className="w-full border-2 border-zinc-300 px-3 py-2.5 font-mono text-base focus:outline-none focus:border-zinc-900"
            />
            <p className="text-xs text-zinc-500 mt-1">
              10 cyfr. Musi mieć aktywne uprawnienia w tym środowisku KSeF.
            </p>
          </div>

          {/* Token */}
          <div>
            <label
              htmlFor="ksef-token"
              className="block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5"
            >
              Token KSeF
              {settings?.has_token && (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono normal-case tracking-normal text-emerald-700 bg-emerald-100 px-1.5 py-0.5">
                  <Check className="w-3 h-3" /> Zapisany: {settings.token_masked}
                </span>
              )}
            </label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                id="ksef-token"
                type="password"
                value={form.token}
                onChange={(e) => setForm({ ...form, token: e.target.value })}
                placeholder={settings?.has_token ? "Pozostaw puste, aby zachować" : "Wklej token z Portalu Podatnika"}
                data-testid="ksef-token-input"
                className="w-full border-2 border-zinc-300 pl-10 pr-3 py-2.5 font-mono text-sm focus:outline-none focus:border-zinc-900"
                autoComplete="new-password"
              />
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Token jest szyfrowany at-rest (Fernet). Nigdy nie jest zwracany do przeglądarki.{" "}
              {currentEnv && (
                <a
                  href={currentEnv.portal_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand hover:underline inline-flex items-center gap-0.5"
                >
                  Portal Podatnika ({currentEnv.label})
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap gap-3 pt-2 border-t border-zinc-200">
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="ksef-save-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold uppercase tracking-wider bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {saving ? "Zapisuję…" : "Zapisz ustawienia"}
            </button>
            <button
              onClick={handleTest}
              disabled={testing || !settings?.has_token}
              data-testid="ksef-test-btn"
              title={
                !settings?.has_token
                  ? "Najpierw zapisz token"
                  : "Wykonaj pełny handshake auth: challenge → RSA-OAEP → ksef-token → redeem"
              }
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold uppercase tracking-wider border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-zinc-900"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {testing ? "Łączę…" : "Testuj połączenie"}
            </button>
          </div>

          {testResult && (
            <div
              data-testid="ksef-test-result"
              className={`border-2 px-4 py-3 text-sm ${
                testResult.ok
                  ? "border-emerald-600 bg-emerald-50 text-emerald-900"
                  : "border-red-600 bg-red-50 text-red-900"
              }`}
            >
              <div className="flex items-center gap-2 font-bold mb-1">
                {testResult.ok ? (
                  <ShieldCheck className="w-4 h-4" />
                ) : (
                  <AlertCircle className="w-4 h-4" />
                )}
                {testResult.ok ? "Uwierzytelnianie udane" : "Uwierzytelnianie nieudane"}
              </div>
              <div className="text-xs font-mono opacity-80 whitespace-pre-wrap">
                {testResult.ok ? (
                  <>
                    Context NIP: {testResult.context_nip}
                    {"\n"}Reference: {testResult.reference_number}
                    {testResult.access_token_expires_at && (
                      <>
                        {"\n"}accessToken ważny do: {testResult.access_token_expires_at}
                      </>
                    )}
                  </>
                ) : (
                  testResult.message
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — status + help */}
      <aside className="space-y-4">
        <div className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#18181b]">
          <div className="px-4 py-3 border-b-2 border-zinc-900 bg-zinc-50">
            <h3 className="font-heading font-black uppercase tracking-tight text-sm">Status</h3>
          </div>
          <dl className="p-4 space-y-2.5 text-xs font-mono">
            <div>
              <dt className="text-zinc-500 uppercase tracking-wider">Aktualne środowisko</dt>
              <dd className="text-zinc-900 font-bold mt-0.5">
                {settings?.environment
                  ? environments.find((e) => e.key === settings.environment)?.label ||
                    settings.environment
                  : "—"}
              </dd>
            </div>
            <div>
              <dt className="text-zinc-500 uppercase tracking-wider">NIP kontekstu</dt>
              <dd className="text-zinc-900 font-bold mt-0.5">{settings?.nip || "—"}</dd>
            </div>
            <div>
              <dt className="text-zinc-500 uppercase tracking-wider">Token</dt>
              <dd className="text-zinc-900 font-bold mt-0.5">
                {settings?.token_masked || <span className="text-red-600">Brak</span>}
              </dd>
            </div>
            <div>
              <dt className="text-zinc-500 uppercase tracking-wider">Ostatnie połączenie</dt>
              <dd className="text-zinc-900 font-bold mt-0.5">
                {settings?.last_connected_at
                  ? new Date(settings.last_connected_at).toLocaleString("pl-PL")
                  : "—"}
              </dd>
            </div>
            {settings?.last_connect_error && (
              <div>
                <dt className="text-red-600 uppercase tracking-wider">Ostatni błąd</dt>
                <dd className="text-red-800 mt-0.5 whitespace-pre-wrap break-all">
                  {settings.last_connect_error}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="bg-amber-50 border-2 border-amber-400 p-4 text-xs">
          <div className="font-bold uppercase tracking-wider text-amber-900 mb-2 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> Skąd wziąć token?
          </div>
          <ol className="space-y-1.5 text-amber-900 list-decimal list-inside">
            <li>Zaloguj się w Portalu Podatnika (link po prawej pod polem tokenu).</li>
            <li>Menu: Uprawnienia → Tokeny → Wygeneruj nowy token.</li>
            <li>Nadaj uprawnienia (co najmniej: <span className="font-mono">Wystawianie faktur</span>, <span className="font-mono">Dostęp do faktur</span>).</li>
            <li>Skopiuj token do pola powyżej i zapisz.</li>
            <li>Kliknij {"„"}Testuj połączenie{"\""}.</li>
          </ol>
        </div>
      </aside>
    </div>
  );
}
